import Button from '@repo/ui/button'
import {
  DeleteIcon,
  DocumentIcon,
  OpenEyeIcon,
  PenIcon,
  PlusIcon,
  UploadIcon,
  UserIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  assignTeflToCenterGroup,
  createCenterGroup,
  createCenterGroupDocument,
  deleteCenterGroup,
  getCenterGroups,
  unassignTeflFromCenterGroup,
  updateCenterGroup
} from '../../../api/centers/center-groups'
import AssignTeflToCenterGroupForm from '../../../components/pages/centers/center-groups/assign-tefl-to-center-group-form'
import CenterGroupCard from '../../../components/pages/centers/center-groups/center-group-card'
import CenterGroupEnrollments from '../../../components/pages/centers/center-groups/center-group-enrollments'
import CenterGroupForm from '../../../components/pages/centers/center-groups/center-group-form'
import UploadCenterGroupDocumentForm from '../../../components/pages/centers/center-groups/upload-center-group-document-form'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { CenterGroup } from '../../../lib/types/models/center-group/center-group'
import { CenterGroupInput } from '../../../lib/types/models/center-group/center-group-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

type CenterGroupPermission =
  | 'create'
  | 'update'
  | 'delete'
  | 'assign-tefl'
  | 'upload-documents'
  | 'view-students'

type Props = {
  id: number
  mode: 'view' | 'edit'
  permissions?: CenterGroupPermission[]
}

const DEFAULT_PERMISSIONS: CenterGroupPermission[] = [
  'create',
  'update',
  'delete',
  'assign-tefl',
  'upload-documents',
  'view-students'
]

const CenterGroups = ({ id, mode, permissions = DEFAULT_PERMISSIONS }: Props) => {
  const queryClient = useQueryClient()
  const { t, currentLanguage } = useTranslate()
  const isEnglish = currentLanguage === 'en'

  const hasPermission = (permission: CenterGroupPermission) => permissions.includes(permission)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'create' | 'delete' | 'update' | 'view-students' | 'assign-tefl' | 'upload-documents',
    CenterGroup
  >(['create', 'delete', 'update', 'view-students', 'assign-tefl', 'upload-documents'])

  const { data } = useQuery({
    queryKey: ['center-groups', id],
    queryFn: () => getCenterGroups(id)
  })

  const { mutateAsync: createCenterGroupMutation, isPending: isCreatePending } = useMutation({
    mutationFn: (input: CenterGroupInput) => createCenterGroup(id, input),
    onSuccess: () => {
      toast.success('Sous-groupe créé avec succès')
      queryClient.invalidateQueries({ queryKey: ['center-groups', id] })
      closeModel('create')
    },
    onError: showAxiosError
  })

  const { mutateAsync: updateCenterGroupMutation, isPending: isUpdatePending } = useMutation({
    mutationFn: updateCenterGroup,
    onSuccess: () => {
      toast.success('Sous-groupe modifié avec succès')
      queryClient.invalidateQueries({ queryKey: ['center-groups', id] })
      closeModel('update')
    },
    onError: showAxiosError
  })

  const { mutateAsync: deleteCenterGroupMutation, isPending: isDeletePending } = useMutation({
    mutationFn: (groupId: number) => deleteCenterGroup(id, groupId),
    onSuccess: () => {
      toast.success('Sous-groupe supprimé avec succès')
      queryClient.invalidateQueries({ queryKey: ['center-groups', id] })
      closeModel('delete')
    },
    onError: showAxiosError
  })

  const { mutateAsync: assignTeflMutation, isPending: isAssignTeflPending } = useMutation({
    mutationFn: ({ groupId, teflId }: { groupId: number; teflId: number }) =>
      assignTeflToCenterGroup(id, groupId, { teflId }),
    onSuccess: () => {
      toast.success(t('center.groups.assign.tefl.success'))
      queryClient.invalidateQueries({ queryKey: ['center-groups', id] })
      closeModel('assign-tefl')
    },
    onError: showAxiosError
  })

  const { mutateAsync: unassignTeflMutation, isPending: isUnassignTeflPending } = useMutation({
    mutationFn: (groupId: number) => unassignTeflFromCenterGroup(id, groupId),
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('center.groups.unassign.tefl.success'))
      queryClient.invalidateQueries({ queryKey: ['center-groups', id] })
      closeModel('assign-tefl')
    }
  })

  const { mutateAsync: uploadDocumentMutation, isPending: isUploadPending } = useMutation({
    mutationFn: createCenterGroupDocument,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('center.groups.documents.success'))
      queryClient.invalidateQueries({ queryKey: ['center-groups', id] })
      closeModel('upload-documents')
    }
  })

  return (
    <div>
      <div className="h-6" />
      <div className="flex items-center justify-between">
        <Typography variant="h5">{t('center.groups.title')}</Typography>
        {hasPermission('create') && (
          <Button
            variant="primary"
            label="Créer un sous-groupe"
            icon={PlusIcon}
            onClick={() => toggleModal('create')}
          />
        )}
      </div>

      <div className="h-4" />
      {/* 3 cards per row */}
      <div className="grid grid-cols-3 gap-4">
        {data?.data
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          .map((group) => (
            <CenterGroupCard
              key={group.id}
              group={group}
              uploadIcon={mode === 'edit' ? UploadIcon : DocumentIcon}
              onEdit={hasPermission('update') ? () => toggleModal('update', group) : undefined}
              onDelete={hasPermission('delete') ? () => toggleModal('delete', group) : undefined}
              onAssignTefl={
                hasPermission('assign-tefl') ? () => toggleModal('assign-tefl', group) : undefined
              }
              onUploadDocument={
                hasPermission('upload-documents')
                  ? () => toggleModal('upload-documents', group)
                  : undefined
              }
              onViewStudents={
                hasPermission('view-students')
                  ? () => toggleModal('view-students', group)
                  : undefined
              }
            />
          ))}
      </div>

      {/* Create Modal */}
      {hasPermission('create') && (
        <Modal
          icon={PlusIcon}
          isOpen={modalState.create}
          title="Créer un sous-groupe"
          hideActions
          onCancel={() => closeModel('create')}
        >
          <CenterGroupForm
            className={MODAL_BODY_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              isLoading: isCreatePending,
              onConfirm: async (values) => {
                await createCenterGroupMutation(values)
              }
            })}
          />
        </Modal>
      )}

      {/* Update Modal */}
      {hasPermission('update') && selectedData && (
        <Modal
          icon={PenIcon}
          isOpen={modalState.update}
          title="Modifier un sous-groupe"
          hideActions
          onCancel={() => closeModel('update')}
        >
          <CenterGroupForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{ name: selectedData.name, color: selectedData.color }}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('update'),
              isLoading: isUpdatePending,
              onConfirm: async (values) => {
                await updateCenterGroupMutation({
                  ...values,
                  groupId: selectedData.id,
                  centerId: id
                })
              }
            })}
          />
        </Modal>
      )}

      {/* Delete Modal */}
      {hasPermission('delete') && selectedData && (
        <Modal
          icon={DeleteIcon}
          variant="destructive"
          isOpen={modalState.delete}
          title={`Êtes-vous sûr de vouloir supprimer ce groupe '${selectedData.name}' ?`}
          isLoading={isDeletePending}
          onCancel={() => closeModel('delete')}
          actionButtonsOptions={{
            confirmText: 'Supprimer',
            cancelText: 'Annuler',
            onConfirm: async () => {
              await deleteCenterGroupMutation(selectedData.id)
            }
          }}
        />
      )}

      {/* Assign TEFL Modal */}
      {hasPermission('assign-tefl') && selectedData && (
        <Modal
          icon={UserIcon}
          isOpen={modalState['assign-tefl']}
          title={t('center.groups.assign.tefl.title')}
          hideActions
          onCancel={() => closeModel('assign-tefl')}
        >
          <AssignTeflToCenterGroupForm
            className={MODAL_BODY_CLASSNAME}
            centerId={id}
            defaultValues={selectedData.teflId ? { teflId: selectedData.teflId } : undefined}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('assign-tefl'),
              isLoading: isAssignTeflPending || isUnassignTeflPending,
              isEnglish,
              onConfirm: async (values) => {
                if (!values.teflId) {
                  if (selectedData.teflId) await unassignTeflMutation(selectedData.id)
                  else closeModel('assign-tefl')
                } else
                  await assignTeflMutation({ groupId: selectedData.id, teflId: values.teflId })
              }
            })}
          />
        </Modal>
      )}

      {/* Upload Documents Modal */}
      {hasPermission('upload-documents') && selectedData && (
        <Modal
          hideActions
          icon={DocumentIcon}
          isOpen={modalState['upload-documents']}
          title={t('center.groups.documents.title')}
          onCancel={() => closeModel('upload-documents')}
        >
          <UploadCenterGroupDocumentForm
            mode={mode}
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{ documents: selectedData.documents }}
            onCancel={() => closeModel('upload-documents')}
            buttonsConfig={modalButtonConfig({
              isLoading: isUploadPending,
              isEnglish,
              onCancel: () => closeModel('upload-documents'),
              onConfirm:
                mode === 'edit'
                  ? async (values) => {
                      await uploadDocumentMutation({
                        groupId: selectedData.id,
                        centerId: id,
                        ...values
                      })
                    }
                  : undefined
            })}
          />
        </Modal>
      )}

      {hasPermission('view-students') && selectedData && (
        <Modal
          hideActions
          icon={OpenEyeIcon}
          size="large"
          isOpen={modalState['view-students']}
          title={t('center.groups.view.students.title', { name: selectedData.name })}
          onCancel={() => closeModel('view-students')}
        >
          <CenterGroupEnrollments
            className={MODAL_BODY_CLASSNAME}
            centerId={id}
            groupId={selectedData.id}
            onClose={() => closeModel('view-students')}
          />
        </Modal>
      )}
    </div>
  )
}

export default CenterGroups
